import { getGraphQLQueryString, getQueryBody } from '@takeshape/graphql';
import {
  findQueryByShape,
  getDefaultMutationName,
  getIdField,
  getQuery,
  getShapeById,
  hasArg,
  isCachedShape,
  isModelShape,
  type ProjectSchemaJSON,
  parsePropertyRef,
  type ResolverWithDefaultMutation,
  type ShapeJSON
} from '@takeshape/schema';
import { normalizeResponse } from '../common/utils/graphql-helpers/index.js';
import type { NormalizedContentById } from '../common/utils/graphql-helpers/normalize-response.js';
import { mutateAdmin, mutateUser, queryUser } from '../graphql-client.js';
import type { StructuralChange } from '../schema-form/types.js';
import type { Content, ContentId, Lock } from './types.js';

export const LOCK_MUTATION = /* GraphQL */ `
mutation ($shapeId: ID, $contentId: ID) {
  lock: createLock(shapeId: $shapeId, id: $contentId) {
    user {
      id
      email
      fullName
    }
    expires
  }
}
`;

export const REMOVE_LOCK_MUTATION = /* GraphQL */ `
mutation ($shapeId: ID, $contentId: ID) {
  success: removeLock(shapeId: $shapeId, id: $contentId)
}
`;

export const BREAK_LOCK_MUTATION = /* GraphQL */ `
mutation ($shapeId: ID, $contentId: ID) {
  success: breakLock(shapeId: $shapeId, id: $contentId)
}
`;

export async function createLock(_: unknown, ids: ContentId): Promise<{ lock: Lock }> {
  return mutateAdmin<{ lock: Lock }>(LOCK_MUTATION)({
    shapeId: ids.shapeId,
    contentId: ids.contentId
  });
}

export async function removeLock(_: unknown, ids: ContentId): Promise<{ success: boolean }> {
  return mutateAdmin<{ success: boolean }>(REMOVE_LOCK_MUTATION)({
    shapeId: ids.shapeId,
    contentId: ids.contentId
  });
}

export async function breakLock(_: unknown, ids: ContentId): Promise<{ success: boolean }> {
  return mutateAdmin<{ success: boolean }>(BREAK_LOCK_MUTATION)({
    shapeId: ids.shapeId,
    contentId: ids.contentId
  });
}

export const NO_FORM_DISPLAYED_TYPES = ['string', 'integer', 'boolean'];
export const NO_FORM_IGNORED_PROPERTIES = ['defaultCursor'];

export type ContentQueryParams = {
  shapeId: string;
  contentId?: string;
  locale: string;
  projectSchema: ProjectSchemaJSON;
};

export type ContentMutationParams = {
  input?: Content;
  structure?: StructuralChange[];
} & ContentQueryParams;

export function searchQueryTemplate(shape: ShapeJSON, body: string, contentId?: string) {
  const where = contentId === undefined ? '' : `where:{_id:{eq:"${contentId}"}}`;
  return /* GraphQL */ `
  {
    search(shapeNames:"${shape.name}"${where}) {
      total
      results {
        ... on ${shape.name} {
          ${body}
        }
      }
    }
  }
  `;
}

export async function getContentQuery({ shapeId, contentId, locale, projectSchema }: ContentQueryParams): Promise<{
  contentId: string;
  response: NormalizedContentById;
}> {
  contentId ??= shapeId;
  const shape = getShapeById(projectSchema, shapeId);
  if (!shape) {
    throw new Error(`Failed to get query shape with id ${shapeId} not found`);
  }

  let queryName: string | undefined;
  let args: Record<string, unknown>;

  if (isModelShape(shape)) {
    queryName = findQueryByShape(projectSchema, shape.name, 'shapedb:get');
    args = {
      locale,
      enableLocaleFallback: false
    };

    if (shape.model?.type !== 'single') {
      args._id = contentId;
    }
  } else if (isCachedShape(shape)) {
    queryName = shape.loaders.get?.query;

    const cacheIdField = getIdField(shape.cache);
    let argName = cacheIdField;

    if (queryName) {
      const query = getQuery(projectSchema, queryName);
      if (query && hasArg(projectSchema, query.query, cacheIdField)) {
        argName = cacheIdField;
      } else {
        throw new Error(`Query "${queryName}" does not have arg "${cacheIdField}"`);
      }
    }

    args = { [argName]: contentId };
  } else {
    throw new Error(`Unexpected error: unable to get query for shape "${shape.name}"`);
  }

  const form = projectSchema.forms?.[shape.name]?.default;

  let query;
  if (queryName && form) {
    query = getGraphQLQueryString(projectSchema, queryName, {
      args,
      maxQueryDepth: 3,
      ignoreGenerated: true
    });
  } else {
    // TODO Setting the depth limit to 1 because querying beyond the indexing depth causes an error
    // https://app.shortcut.com/takeshape/story/6692/support-deep-querying-of-indexed-data
    const body = getQueryBody(shape.id, projectSchema, { maxQueryDepth: 1 });
    query = searchQueryTemplate(shape, body, contentId);
  }

  const res = await queryUser(query)();

  let data: Content;

  if (queryName && form) {
    const propertyRef = parsePropertyRef(queryName);
    const actualQueryName = propertyRef?.propertyName ?? queryName;
    data = res[actualQueryName];
  } else {
    data = res.search.results[0];
  }

  const response = data ? normalizeResponse(data, projectSchema) : { [shapeId]: { [contentId]: null } };

  return {
    contentId,
    response
  };
}

function createMutationHandler<T>(resolverName: ResolverWithDefaultMutation) {
  return async ({
    input = {},
    structure = [],
    contentId,
    shapeId,
    locale,
    projectSchema
  }: ContentMutationParams): Promise<{ contentId: string; response: T }> => {
    const shape = getShapeById(projectSchema, shapeId);
    if (!shape) {
      throw new Error(`Failed to get query shape with id ${shapeId} not found`);
    }

    const operation = getDefaultMutationName(shape.name, resolverName);
    const isDelete = resolverName === 'shapedb:delete';

    if (shape.model?.type === 'single') {
      delete input._id;
    } else if (contentId) {
      input = { ...input, _id: contentId };
    }

    const mutation = getGraphQLQueryString(projectSchema, `Mutation.${operation}`, { allVars: true, maxQueryDepth: 4 });

    const res = await mutateUser(mutation)({
      input,
      structure,
      locale,
      enableLocaleFallback: false
    });

    const data = res[operation];
    return {
      contentId: data.result._id,
      response: isDelete ? data.result : normalizeResponse(data.result, projectSchema)
    };
  };
}

export const createContentMutation = createMutationHandler<NormalizedContentById>('shapedb:create');
export const updateContentMutation = createMutationHandler<NormalizedContentById>('shapedb:update');
export const deleteContentMutation = createMutationHandler<boolean>('shapedb:delete');
export const duplicateContentMutation = createMutationHandler<NormalizedContentById>('shapedb:duplicate');
